import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '../utils/api';
import { ArrowLeft, Upload, X, FileText, Loader2, Plus, Minus } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

const UploadDocument = () => {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [metaFields, setMetaFields] = useState([{ key: '', value: '' }]);
  const [form, setForm] = useState({
    title: '', description: '', categoryId: '', status: 'draft', tags: ''
  });

  useEffect(() => { api.get('/documents/categories').then(r => setCategories(r.data.categories)); }, []);

  const onDrop = useCallback((accepted) => {
    if (accepted[0]) {
      setFile(accepted[0]);
      if (!form.title) setForm(f => ({ ...f, title: accepted[0].name.replace(/\.[^/.]+$/, '') }));
    }
  }, [form.title]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
      'text/csv': ['.csv'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif'],
    }
  });


  // Handling the presigned url generation.
  const fetchPresignedUrl = async () => {
    try {
      const res = await api.post('/documents/presigned-url', { fileName: file?.name, fileType: file?.type, slug: JSON.parse(localStorage.getItem('dms_tenant')).slug });
      return res?.data;

    } catch (err) {
      console.error(err);
      toast.error("Something went wrong in uploading the file.");
    }
  }

  

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select a file'); return; }
    if (!form.title) { toast.error('Title is required'); return; }

    setLoading(true);
    

    try { 
      //Uploading the file directly to S3 bucket from here.
      //Fetch the presigned uri.
      const { presignedUrl, s3_fileName, fileUrl } = await fetchPresignedUrl();
      console.log(presignedUrl, s3_fileName, fileUrl)

      console.log("Presigned URL: ", presignedUrl);

      //API call to presigned url to upload the file here.
      const response = await axios.put(presignedUrl, file);
      console.log(response);

      if(response.status !== 200){
        toast.error("Something went wrong in uploading the file.");
        return;
      }

      
      //Creating the database entry.
      const res = await api.post('/documents/create', {
        title: form.title,
        description: form.description,
        categoryId: form.categoryId,
        status: form.status,
        tags: form.tags,
        metadata: JSON.stringify(metaFields),
        fileName: file?.name,
        s3_fileName: s3_fileName,
        fileUrl: fileUrl,
        fileSize: file?.size,
        fileType: file?.type
      });

      console.log(res);
      if(res.status !== 201){
        toast.error("Something went wrong in creating the document.");
        return;
      }
      toast.success('Document uploaded successfully!');
      navigate(`/app/documents/${res.data.documentId}`);
    }
    catch (err) {
      console.error(err);
      toast.error("Something went wrong in uploading the file.");
    }
    finally {
      setLoading(false);
    }
  };

  const addMetaField = () => setMetaFields(f => [...f, { key: '', value: '' }]);
  const removeMetaField = (i) => setMetaFields(f => f.filter((_, idx) => idx !== i));
  const updateMeta = (i, field, val) => setMetaFields(f => f.map((m, idx) => idx === i ? { ...m, [field]: val } : m));

  const formatSize = (bytes) => bytes < 1048576 ? (bytes / 1024).toFixed(1) + ' KB' : (bytes / 1048576).toFixed(1) + ' MB';

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/app/documents" className="btn-secondary !py-1.5 !px-3"><ArrowLeft className="w-4 h-4" /></Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Upload Document</h1>
          <p className="text-gray-500 text-sm">Add a new document to your workspace</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Dropzone */}
        <div {...getRootProps()} className={`file-drop-zone rounded-xl p-10 text-center cursor-pointer ${isDragActive ? 'active' : ''}`}>
          <input {...getInputProps()} />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText className="w-8 h-8 text-primary-500" />
              <div className="text-left">
                <p className="font-medium text-gray-900">{file.name}</p>
                <p className="text-sm text-gray-400">{formatSize(file.size)}</p>
              </div>
              <button type="button" onClick={(e) => { e.stopPropagation(); setFile(null); }}
                className="ml-4 p-1 text-gray-400 hover:text-red-500 rounded-full hover:bg-red-50">
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div>
              <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="font-medium text-gray-700">{isDragActive ? 'Drop your file here' : 'Drag & drop or click to browse'}</p>
              <p className="text-sm text-gray-400 mt-1">PDF, DOCX, XLSX, PPTX, TXT, CSV, Images up to 50MB</p>
            </div>
          )}
        </div>

        <div className="card space-y-4">
          <h2 className="font-semibold text-gray-800">Document Details</h2>
          <div>
            <label className="label">Title *</label>
            <input className="input" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} required placeholder="Document title" />
          </div>
          <div>
            <label className="label">Description</label>
            <textarea className="input resize-none" rows={3} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category</label>
              <select className="input" value={form.categoryId} onChange={e => setForm(f => ({ ...f, categoryId: e.target.value }))}>
                <option value="">No Category</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Status</label>
              <select className="input" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Tags (comma-separated)</label>
            <input className="input" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} placeholder="finance, q4, report" />
          </div>
        </div>

        {/* Custom Metadata */}
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-gray-800">Custom Metadata</h2>
            <button type="button" onClick={addMetaField} className="text-primary-600 hover:text-primary-700 text-sm flex items-center gap-1">
              <Plus className="w-4 h-4" /> Add Field
            </button>
          </div>
          {metaFields.map((meta, i) => (
            <div key={i} className="flex gap-2">
              <input className="input" placeholder="Field name" value={meta.key} onChange={e => updateMeta(i, 'key', e.target.value)} />
              <input className="input" placeholder="Value" value={meta.value} onChange={e => updateMeta(i, 'value', e.target.value)} />
              {metaFields.length > 1 && (
                <button type="button" onClick={() => removeMetaField(i)} className="p-2 text-gray-400 hover:text-red-500">
                  <Minus className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>

        <div className="flex gap-3 justify-end">
          <Link to="/app/documents" className="btn-secondary">Cancel</Link>
          <button type="submit" disabled={loading || !file} className="btn-primary disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Uploading...' : 'Upload Document'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default UploadDocument;
